import {
	Breadcrumb,
	DefaultButton,
	Dialog,
	DialogType,
	IBreadcrumbItem,
	PrimaryButton,
	Spinner,
	Stack,
} from '@fluentui/react';
import {UserContextType} from 'authentication';
import {useUserContext} from 'authentication/UserContext';
import {FaqTooltipTranslationProvider} from 'components/Faq/FaqTooltipTranslationProvider';
import {LoadWrapper} from 'components/LoadWrapper';
import {BaselinesSelectionDialog} from 'features/Baselines/BaselineComparisonPage/BaselinesSelectionDialog/BaselinesSelectionDialog';
import {BaselinesList} from 'features/Baselines/BaselineListPage/BaselinesList';
import {
	translationNamespace,
	useBaselineListPageTranslations,
} from 'features/Baselines/BaselineListPage/BaselinesPage.utils';
import {BaselineCreateDialog} from 'features/Baselines/components/BaselineCreateDialog';
import {BaselineRenameDialog} from 'features/Baselines/components/BaselineRenameDialog';
import {
	createPathWithTooltipsTextKey,
	createTooltipTranslationProviderFromNamespace,
} from 'features/localizedTooltips';
import {useVehicleProjectsTranslation} from 'features/VehicleProjects/hooks/vehicleProjectsTranslationHooks';
import {useCommand} from 'hooks';
import React, {useEffect, useMemo, useState} from 'react';
import {NavigateFunction, useNavigate} from 'react-router-dom';
import {useRequirementsTranslation} from '../hooks/useRequirementsTranslation';
import {Baseline} from 'types';
import {
	useCreateBaseLineByModelAndSeriesMutation,
	useCreateRequirementsLibraryBaseLineMutation,
	useGetBaselinesByModelAndSeriesQuery,
} from 'features/Baselines/hooks/baseline.generated';
import {useBoolean} from '@fluentui/react-hooks';

interface IRequirementsBaseline {}

const TranslationProvider =
	createTooltipTranslationProviderFromNamespace(translationNamespace);
const params = {modelSeries: 'Anforderungsbibliothek', generation: '-'};

export const RequirementsBaseline: React.FC<IRequirementsBaseline> = () => {
	const {isAdmin, isBedarfskoordinator}: UserContextType = useUserContext();

	const {t} = useVehicleProjectsTranslation({keyPrefix: 'BaselineCmds'});

	const {t: tCreate} = useVehicleProjectsTranslation({
		keyPrefix: 'BaselineCreateDialog',
	});

	const navigate: NavigateFunction = useNavigate();

	const [
		saveDialogHidden,
		{setTrue: hideSaveDialog, setFalse: showSaveDialog},
	] = useBoolean(true);

	const [createRequirementsLibraryBaseLineMutation, {loading: creatingBaseline}] =
		useCreateRequirementsLibraryBaseLineMutation();

	useCommand(
		{
			key: 'baselines',
			text: t('ViewBaselineCmds'),
			iconProps: {iconName: 'History'},
			subMenuProps: {
				items: [
					{
						key: 'Overview',
						text: t('ViewOverview'),
						onClick() {
							navigate(`./baselines`);
						},
					},
					{
						key: 'Create',
						text: t('Create'),
						onClick() {
							showSaveDialog();
						},
					},
				],
			},
			hidden: !(isAdmin || isBedarfskoordinator),
			priority: 2,
		},
		[],
	);

	const handleSave = async () => {
		const now = new Date();
		const defaultName = `TR:ACE Anforderungsliste vom ${now.toLocaleDateString(
			'de-DE',
		)} um ${now.toLocaleTimeString('default', {
			hour: '2-digit',
			minute: '2-digit',
			hour12: false,
		})} Uhr`;

		await createRequirementsLibraryBaseLineMutation({
			variables: {
				input: {
					name: defaultName,
					generation: params.generation,
					modelSeries: params.modelSeries,
					transferWorkflow: {
						transferToCodebeamer: false,
					},
				},
			},
			refetchQueries: [],
		});
	};

	return (
		<>
			<Dialog
				hidden={saveDialogHidden}
				dialogContentProps={{
					type: DialogType.normal,
					title: tCreate('Title'),
					closeButtonAriaLabel: 'Close',
				}}
			>
				<Stack horizontal tokens={{childrenGap: 8}} style={{padding: 20}}>
					<DefaultButton text={tCreate('CancelBtn')} onClick={hideSaveDialog} />
					<PrimaryButton onClick={handleSave}>
						{creatingBaseline ? <Spinner /> : tCreate('SaveBtn')}
					</PrimaryButton>
				</Stack>
			</Dialog>
		</>
	);
};

export const RequirementsBaselinePage: React.FC = () => {
	const {t} = useRequirementsTranslation();

	const {t: tBaseline} = useBaselineListPageTranslations();
	const navigate: NavigateFunction = useNavigate();

	const breadcrumbs: IBreadcrumbItem[] = [
		{
			text: t('RequirementsHeader'),
			key: 'requirements',
			onClick: () => navigate('/requirements'),
		},
		{
			key: 'baselines',
			text: tBaseline('BreadcrumbText'),
		},
	];

	const translationHookOptions = useMemo(() => {
		return {keyPrefix: createPathWithTooltipsTextKey('Columns')};
	}, []);

	const [baselines, setBaselines] = useState<Baseline[]>([]);

	const {data, loading, startPolling, stopPolling} =
		useGetBaselinesByModelAndSeriesQuery({
			variables: params,
			fetchPolicy: 'no-cache',
		});

	const baselineList = data?.vehicleProjectBaselinesByModelAndSeries ?? [];

	return (
		<>
			<Breadcrumb items={breadcrumbs} />
			<LoadWrapper loading={loading}>
				<TranslationProvider translationHookOptions={translationHookOptions}>
					<BaselinesList
						baselineList={baselineList}
						params={params}
						setBaselines={setBaselines}
					/>
				</TranslationProvider>
			</LoadWrapper>
		</>
	);
};
